import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Search, Plus, QrCode, Ban, RefreshCw, X, ShieldCheck, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import styles from './GatePasses.module.css';
import api from '../../api/axios';

const GatePasses = () => {
  const [passes, setPasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedPass, setSelectedPass] = useState(null);
  const [showQrModal, setShowQrModal] = useState(false);

  useEffect(() => {
    fetchPasses();
  }, []);

  const fetchPasses = async () => {
    try {
      const { data } = await api.get('/admin/gate-passes');
      setPasses(data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to fetch gate passes');
      setLoading(false);
    }
  };

  const confirmAction = (message, action, btnText, isDanger = true) => {
    toast((t) => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontFamily: 'Inter, sans-serif' }}>
        <p style={{ margin: 0, color: '#ffffff', fontSize: '14px', fontWeight: '500' }}>{message}</p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button 
            style={{ padding: '6px 12px', background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '6px', color: '#475569', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
            onClick={() => toast.dismiss(t.id)}
          >
            Cancel
          </button>
          <button 
            style={{ padding: '6px 12px', background: isDanger ? '#ef4444' : '#166534', border: 'none', borderRadius: '6px', color: 'white', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
            onClick={() => {
              toast.dismiss(t.id);
              action();
            }}
          >
            {btnText}
          </button>
        </div>
      </div>
    ), { duration: Infinity, position: 'top-center' });
  };

  const handleRevoke = (id) => {
    confirmAction('Are you sure you want to revoke this pass?', async () => {
      try {
        await api.put(`/admin/gate-passes/${id}/revoke`);
        toast.success('Gate pass revoked');
        fetchPasses();
      } catch (error) {
        toast.error('Failed to revoke pass');
      }
    }, 'Revoke', true);
  };

  const handleDelete = (id) => {
    confirmAction('Are you sure you want to permanently delete this gate pass?', async () => {
      try {
        await api.delete(`/admin/gate-passes/${id}`);
        toast.success('Gate pass deleted');
        setPasses(passes.filter(p => p._id !== id));
      } catch (error) {
        toast.error('Failed to delete pass');
      }
    }, 'Delete', true);
  };

  const handleRegenerate = (id) => {
    confirmAction('This will revoke the old pass and generate a new one. Continue?', async () => {
      try {
        await api.put(`/admin/gate-passes/${id}/regenerate`);
        toast.success('Gate pass regenerated');
        fetchPasses();
      } catch (error) {
        toast.error('Failed to regenerate pass');
      }
    }, 'Regenerate', false);
  };

  const filteredPasses = passes.filter(p => {
    const residentName = p.resident?.fullName || p.name;
    return p.passId.toLowerCase().includes(search.toLowerCase()) ||
    residentName.toLowerCase().includes(search.toLowerCase()) ||
    (p.flat && p.flat.toLowerCase().includes(search.toLowerCase()));
  });

  const stats = {
    total: passes.length,
    active: passes.filter(p => p.status === 'Active').length,
    revoked: passes.filter(p => p.status === 'Revoked').length,
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1><ShieldCheck size={28} color="#166534" /> Gate Pass Management</h1>
        <p>Manage and monitor resident gate passes</p>
      </div>

      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{stats.total}</div>
          <div className={styles.statLabel}>Total Passes</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue} style={{color: '#166534'}}>{stats.active}</div>
          <div className={styles.statLabel}>Active Passes</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue} style={{color: '#991b1b'}}>{stats.revoked}</div>
          <div className={styles.statLabel}>Revoked Passes</div>
        </div>
      </div>

      <div className={styles.mainCard}>
        <div className={styles.cardHeader}>
          <div className={styles.searchBar}>
            <input 
              type="text" 
              placeholder="Search Pass ID, Name, Flat..." 
              className={styles.searchInput}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <button className={styles.btnPrimary} onClick={() => toast('Auto-generation is enabled. Use manual generation only for special cases.')}>
            <Plus size={18} /> Generate Pass
          </button>
        </div>

        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Pass ID</th>
                <th>Resident</th>
                <th>Flat / Block</th>
                <th>Vehicle</th>
                <th>Status</th>
                <th>Generated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" style={{textAlign:'center'}}>Loading...</td></tr>
              ) : filteredPasses.length === 0 ? (
                <tr><td colSpan="7" style={{textAlign:'center'}}>No passes found</td></tr>
              ) : filteredPasses.map(p => (
                <tr key={p._id}>
                  <td><strong>{p.passId}</strong></td>
                  <td>{p.resident?.fullName || p.name}</td>
                  <td>{p.flat} {p.block && `(${p.block})`}</td>
                  <td>{p.vehicleNumber || '—'}</td>
                  <td>
                    <span className={`${styles.badge} ${p.status === 'Active' ? styles.badgeActive : p.status === 'Revoked' ? styles.badgeRevoked : styles.badgeExpired}`}>
                      {p.status}
                    </span>
                  </td>
                  <td>{new Date(p.validFrom).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                  <td>
                    <div className={styles.actions}>
                      <button className={styles.btnAction} title="View QR" onClick={() => { setSelectedPass(p); setShowQrModal(true); }}>
                        <QrCode size={18} />
                      </button>
                      <button className={styles.btnAction} title="Regenerate" onClick={() => handleRegenerate(p._id)}>
                        <RefreshCw size={18} />
                      </button>
                      {p.status === 'Active' && (
                        <button className={`${styles.btnAction} ${styles.btnDanger}`} title="Revoke" onClick={() => handleRevoke(p._id)}>
                          <Ban size={18} />
                        </button>
                      )}
                      <button className={`${styles.btnAction} ${styles.btnDanger}`} title="Delete" onClick={() => handleDelete(p._id)}>
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className={styles.mobileList}>
            {loading ? (
              <div style={{padding: '20px', textAlign: 'center'}}>Loading...</div>
            ) : filteredPasses.length === 0 ? (
              <div style={{padding: '20px', textAlign: 'center'}}>No passes found</div>
            ) : filteredPasses.map(p => (
              <div key={p._id} className={styles.mobileCard}>
                <div className={styles.cardTop}>
                  <div className={styles.passMeta}>
                    <strong>{p.resident?.fullName || p.name}</strong>
                    <span>Pass ID: {p.passId}</span>
                  </div>
                  <span className={`${styles.badge} ${p.status === 'Active' ? styles.badgeActive : p.status === 'Revoked' ? styles.badgeRevoked : styles.badgeExpired}`}>
                    {p.status}
                  </span>
                </div>
                <div className={styles.cardMid}>
                  <p><strong>Flat:</strong> {p.flat} {p.block && `(${p.block})`}</p>
                  {p.vehicleNumber && <p><strong>Vehicle:</strong> {p.vehicleNumber}</p>}
                  <p><strong>Date:</strong> {new Date(p.validFrom).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                </div>
                <div className={styles.cardBottom}>
                  <div className={styles.actions}>
                    <button className={styles.btnAction} title="View QR" onClick={() => { setSelectedPass(p); setShowQrModal(true); }}>
                      <QrCode size={18} />
                    </button>
                    <button className={styles.btnAction} title="Regenerate" onClick={() => handleRegenerate(p._id)}>
                      <RefreshCw size={18} />
                    </button>
                    {p.status === 'Active' && (
                      <button className={`${styles.btnAction} ${styles.btnDanger}`} title="Revoke" onClick={() => handleRevoke(p._id)}>
                        <Ban size={18} />
                      </button>
                    )}
                    <button className={`${styles.btnAction} ${styles.btnDanger}`} title="Delete" onClick={() => handleDelete(p._id)}>
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {showQrModal && selectedPass && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '24px'}}>
              <h2 style={{margin:0, fontSize: '20px'}}>Resident Gate Pass</h2>
              <button onClick={() => setShowQrModal(false)} style={{background:'transparent', border:'none', cursor:'pointer'}}><X size={24}/></button>
            </div>
            
            <div style={{padding: '24px', background: '#f8fafc', borderRadius: '16px', display: 'inline-block', marginBottom: '24px'}}>
              <QRCodeSVG 
                value={selectedPass.qrToken} 
                size={200} 
                level="H"
                includeMargin={true}
              />
            </div>
            
            <h3 style={{fontSize: '24px', margin: '0 0 8px 0'}}>{selectedPass.resident?.fullName || selectedPass.name}</h3>
            <p style={{margin: '0 0 4px 0', color: '#64748b'}}>Flat {selectedPass.flat} • Block {selectedPass.block}</p>
            <p style={{margin: '0 0 16px 0', color: '#64748b'}}>ID: {selectedPass.passId}</p>
            
            <span className={`${styles.badge} ${selectedPass.status === 'Active' ? styles.badgeActive : styles.badgeRevoked}`}>
              {selectedPass.status}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default GatePasses;
